// Shared utilities for the PQRSD Test Bench (lab module).
//
// These mirror the real PQRSD call paths so the bench exercises the live
// backend the same way the production UI does:
//   - intakePqrsd()    -> mirrors PqrsdForm.handleSubmit (FormData + AppCheck +
//                         X-Idempotency-Key against REACT_APP_PQRSD_INTAKE_URL).
//   - callPqrsdPublic() -> mirrors PqrsdTrackPage.callApi (JSON + AppCheck
//                         against REACT_APP_PQRSD_TRACK_URL).
// Plus tiny assertion helpers and a cleanup routine that deletes the tagged
// `filed` docs the bench created.

import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { getToken } from "firebase/app-check";
import { firestore, getAppCheckInstance } from "../../config/ConfigFirebase";
import { pqrsdFunctionUrl } from "../../services/backend/PqrsdService";
import { getToken as getAuthIdToken } from "../../services/auth/AuthServices";

// Every PQRSD the bench files gets this subject prefix so cleanup can find and
// delete leftovers safely, even across sessions.
export const LAB_TAG = "[LAB-TEST]";

// Placeholder the public form stamps on solicitor fields when anonymous.
const NA = "No aplica";

const COLLECTION = "filed";

// --- AppCheck -------------------------------------------------------------

// Fresh AppCheck token, same pattern the form/track page use (forceRefresh).
export async function appCheckToken() {
  const instance = getAppCheckInstance();
  if (!instance) throw new Error("App Check no está inicializado");
  const { token } = await getToken(instance, true);
  if (!token) throw new Error("Token de App Check no disponible");
  return token;
}

// --- Emails ---------------------------------------------------------------

// Build a `local+labtest-<tag>@domain` alias so each test submission has a
// unique email (dodging the 5-per-email/10-min intake rate cap) while all the
// confirmation mail still lands in the tester's real inbox.
export function aliasEmail(base, tag) {
  const src = base && base.includes("@") ? base : "labtester@example.com";
  const [local, domain] = src.split("@");
  const cleanTag = String(tag ?? Date.now())
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase()
    .slice(0, 24);
  return `${local}+labtest-${cleanTag}@${domain}`;
}

// --- Payload --------------------------------------------------------------

// Bogota "DD/MM/YYYY HH:MM" string, mirroring PqrsdForm.formatBogotaDateTime.
function formatBogota(ms = Date.now()) {
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ms));
}

// Minimal valid intake payload matching PqrsdForm.buildPayload's nested shape:
// the outer object is { pqrsd: <finalPayload>, meta }, where finalPayload holds
// the citizen fields. The owning internal user is resolved server-side.
export function buildIntakePayload({
  type,
  anonymous = false,
  email,
  subject,
  body,
}) {
  const createdAt = Timestamp.now();
  const finalPayload = {
    anonymous: anonymous ? "Sí" : "No",
    email,
    pqrsd: { type, subject, body: body || subject },
    dataPolicy: "Acepto",
    attachments: [],
    createdAt,
    fecha: formatBogota(createdAt.toMillis()),
  };

  finalPayload.solicitor = {
    name: anonymous ? NA : "Laboratorio",
    surname: anonymous ? NA : "Pruebas",
    idType: anonymous ? NA : "Cédula de Ciudadanía",
    idNumber: anonymous ? NA : "1000000001",
    solicitorType: anonymous ? NA : "Persona natural",
  };
  // Flat compatibility fields handlePqrsd also reads.
  finalPayload.pqrsd.type_person = anonymous ? NA : "Persona natural";
  finalPayload.pqrsd.nombre_solicitante = anonymous ? NA : "Laboratorio";
  finalPayload.pqrsd.apellido_solicitante = anonymous ? NA : "Pruebas";
  finalPayload.pqrsd.tipo_id = anonymous ? NA : "Cédula de Ciudadanía";
  finalPayload.pqrsd.num_id = anonymous ? NA : "1000000001";

  return {
    pqrsd: finalPayload,
    meta: { constants: { source: "lab-test-bench", version: "2.0" } },
  };
}

// --- Cloud Function calls -------------------------------------------------

// Files the PQRSD via the real intakePqrsd cloud function. Returns
// { ok, status, json } where json is { result, flowId, radicado, referenceId,
// replay? } on success or { result:false, error } on failure.
//
// authenticated (default true) attaches the tester's real Firebase ID token,
// the same exemption ventanilla uses to bypass the IP/email rate limit — so
// bench runs aren't throttled by the shared dev machine IP. The "rate-limit"
// test passes authenticated:false on purpose, to still exercise the real
// (unauthenticated) public limiter.
export async function intakePqrsd({
  type,
  anonymous = false,
  email,
  subject,
  body,
  files = [],
  idempotencyKey,
  authenticated = true,
} = {}) {
  // Same resolution order as PqrsdForm: explicit env override, else auto-derive
  // from REACT_APP_SERVER ({region}-{projectId}). The URL is never "missing".
  const intakeUrl =
    process.env.REACT_APP_PQRSD_INTAKE_URL ||
    process.env.VITE_PQRSD_INTAKE_URL ||
    pqrsdFunctionUrl("intakePqrsd");

  const token = await appCheckToken();
  const payload = buildIntakePayload({ type, anonymous, email, subject, body });
  payload.pqrsd.attachments = files.map((f) => ({
    name: f.name,
    size: f.size,
    type: f.type,
  }));

  const fd = new FormData();
  fd.append("payload", JSON.stringify(payload));
  files.forEach((f) => fd.append("files[]", f));

  const headers = { "X-Firebase-AppCheck": token };
  if (idempotencyKey) headers["X-Idempotency-Key"] = idempotencyKey;

  if (authenticated) {
    try {
      const idToken = await getAuthIdToken();
      if (idToken) headers.Authorization = "Bearer " + idToken;
    } catch (_) {
      // Non-fatal: falls back to the unauthenticated (rate-limited) path.
    }
  }

  const resp = await fetch(intakeUrl, { method: "POST", headers, body: fd });
  const json = await resp.json().catch(() => ({}));
  return { ok: resp.ok, status: resp.status, json };
}

// Calls handlePqrsdPublic (tracking + citizen actions). Returns
// { ok, status, json }.
export async function callPqrsdPublic(body) {
  // Same resolution as PqrsdTrackPage: env override, else auto-derive from
  // REACT_APP_SERVER so the tracking request always has a valid endpoint.
  const trackUrl =
    process.env.REACT_APP_PQRSD_TRACK_URL ||
    process.env.REACT_APP_PQRSD_PUBLIC_URL ||
    pqrsdFunctionUrl("handlePqrsdPublic");

  const token = await appCheckToken();
  const resp = await fetch(trackUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Firebase-AppCheck": token },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({}));
  return { ok: resp.ok, status: resp.status, json };
}

// Calls pqrsdInfoReply (the citizen side of a requerimiento de información).
// Multipart, because file items are answered with an upload: each file is sent
// under the id of the item it answers, exactly like CitizenInfoRequestForm does.
// `token` is the scoped link token (purpose "info_reply").
export async function callPqrsdInfoReply({ token, action, answers = {}, files = {} } = {}) {
  const url =
    process.env.REACT_APP_PQRSD_INFO_REPLY_URL || pqrsdFunctionUrl("pqrsdInfoReply");
  const appCheck = await appCheckToken();
  const fd = new FormData();
  fd.append("payload", JSON.stringify({ token, action, answers }));
  Object.entries(files).forEach(([itemId, file]) => {
    if (file) fd.append(itemId, file, file.name);
  });
  const resp = await fetch(url, {
    method: "POST",
    headers: { "X-Firebase-AppCheck": appCheck },
    body: fd,
  });
  const json = await resp.json().catch(() => ({}));
  return { ok: resp.ok, status: resp.status, json };
}

// Rewinds a fixture's SLA so a test can tell "resumed" apart from "reset".
// A freshly filed PQRSD still has its whole window, which makes the two
// outcomes identical; burning most of it first makes the difference visible.
// Writes straight to Firestore — lab-only, on dev data the bench itself created.
export async function forceSlaDeadline(radicado, deadline) {
  await updateDoc(doc(firestore, "filed", radicado), {
    "pqrsd.slaDeadline": Timestamp.fromDate(deadline),
  });
}

// Pulls the `t=` token out of a citizen link (the reply endpoint takes the raw
// token, the browser takes the whole URL).
export function tokenFromLink(link) {
  if (!link) return null;
  try {
    return new URL(link).searchParams.get("t");
  } catch {
    const m = String(link).match(/[?&]t=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }
}

// In-memory test attachment (no disk access) for the attachment intake test.
export function makeTestFile(name = "lab-test.txt") {
  const content = `PQRSD lab test attachment generated ${new Date().toISOString()}`;
  return new File([content], name, { type: "text/plain" });
}

// A small file of a MIME type the intake function rejects (for the negative
// annex test). Tiny on purpose — the point is the type, not the size.
export function makeDisallowedFile(name = "lab-test.zip") {
  return new File([new Uint8Array([80, 75, 3, 4])], name, { type: "application/zip" });
}

// --- Assertions -----------------------------------------------------------
// Each returns a row { label, ok, detail } the bench renders with ✓/✗.

function fmt(v) {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  if (typeof v === "string") return `"${v}"`;
  if (typeof v === "object") {
    try {
      return JSON.stringify(v).slice(0, 120);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

export function assert(label, cond, detail) {
  return {
    label,
    ok: !!cond,
    detail: cond ? detail || "" : detail || "condición no cumplida",
  };
}

export function assertEqual(label, actual, expected) {
  const ok = actual === expected;
  return {
    label,
    ok,
    detail: ok
      ? `= ${fmt(expected)}`
      : `esperado ${fmt(expected)}, obtuvo ${fmt(actual)}`,
  };
}

export async function assertThrowsAsync(label, fn, matcher) {
  try {
    await fn();
    return { label, ok: false, detail: "no lanzó error" };
  } catch (e) {
    const msg = e?.message || String(e);
    let ok = true;
    if (typeof matcher === "string") ok = msg.includes(matcher);
    else if (matcher instanceof RegExp) ok = matcher.test(msg);
    return {
      label,
      ok,
      detail: ok ? `lanzó: ${msg}` : `lanzó pero no coincide (${matcher}): ${msg}`,
    };
  }
}

// --- Cleanup --------------------------------------------------------------

// Deletes the session's created radicados. When byTag is true, also sweeps any
// `filed` PQRSD whose subject starts with LAB_TAG (leftovers from prior runs).
// Returns { deleted, failed:[{id,error}], total }.
export async function cleanupLabPqrsd(radicados = [], { byTag = false } = {}) {
  const ids = new Set(radicados.filter(Boolean));

  if (byTag) {
    try {
      const snap = await getDocs(
        query(collection(firestore, COLLECTION), where("filedType", "==", "pqrsd"))
      );
      snap.docs.forEach((d) => {
        const subj = d.data()?.subject || "";
        if (String(subj).startsWith(LAB_TAG)) ids.add(d.id);
      });
    } catch (e) {
      console.warn("cleanupLabPqrsd tag sweep failed (non-fatal):", e?.message);
    }
  }

  let deleted = 0;
  const failed = [];
  for (const id of ids) {
    try {
      await deleteDoc(doc(firestore, COLLECTION, id));
      deleted += 1;
    } catch (e) {
      failed.push({ id, error: e?.message || String(e) });
    }
  }
  return { deleted, failed, total: ids.size };
}
